import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import ExportHistoryTab from './ExportHistoryTab';
import { StyledExportPanel } from './index.style';

const EXPORT_PANEL_TAB = { FORM: 'form', HISTORY: 'history' };

const TABS = [
  { name: EXPORT_PANEL_TAB.FORM, label: 'exportFileTab' },
  { name: EXPORT_PANEL_TAB.HISTORY, label: 'exportHistoryTab' },
];

/**
 * Shell for the export popover: owns the tab bar and the export history tab,
 * and renders the existing export form as `children`.
 *
 * While the flag is off the component returns `children` untouched, so the
 * popover renders the same tree it did before this feature — no wrapper, no
 * request, no interval. The flag itself lives in `useExportHistoryEnabled`,
 * which the export buttons share: the history tab, the per-record progress
 * polling and the server-built SRT are one feature behind one switch.
 *
 * `isOpen` is the popover's own visibility, used to put the panel back on the
 * export form every time it opens.
 */
const ExportPanel = ({ isOpen = true, children }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(EXPORT_PANEL_TAB.FORM);

  // Opening the popover always lands on the export form, whichever tab was left
  // behind last time. The initial state already covers the common case, because
  // the triggers pass `destroyTooltipOnHide` and the panel remounts — but that
  // is the trigger's choice, and rc-tooltip keeps a shown overlay mounted by
  // default, so without this the tab would silently stick on "history" the day
  // a trigger stops destroying its overlay.
  //
  // It watches the panel turning visible, not hidden: rc-trigger skips
  // re-rendering the popup while it is hidden, so a hidden overlay never sees
  // the prop go false — only the next open delivers a new value.
  useEffect(() => {
    if (isOpen) setActiveTab(EXPORT_PANEL_TAB.FORM);
  }, [isOpen]);

  const historyEnabled = useExportHistoryEnabled();

  if (!historyEnabled) return children;

  return (
    <StyledExportPanel>
      <div className="export-panel-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.name}
            type="button"
            className={
              activeTab === tab.name
                ? 'export-panel-tab active'
                : 'export-panel-tab'
            }
            onClick={() => setActiveTab(tab.name)}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>
      {activeTab === EXPORT_PANEL_TAB.FORM ? (
        <div className="export-panel-form">{children}</div>
      ) : (
        <ExportHistoryTab />
      )}
    </StyledExportPanel>
  );
};

export default ExportPanel;
